import React, { useEffect, useState } from 'react';
import axiosInstance from '@/lib/axios';
import { applyThemeColors } from '@/lib/brand-theme';
import { notifyNativeSafeAreaTheme } from '@/lib/native-safe-area-theme';
import { type Tenant, TenantContext } from './tenant-context';

const CACHE_KEY = 'milora_tenant_config';

function normalizeTenant(raw: Tenant): Tenant {
  const { primaryColor: _legacyPrimaryColor, ...tenant } = raw as Tenant & {
    primaryColor?: string | null;
  };
  return tenant;
}

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      return cached ? normalizeTenant(JSON.parse(cached)) : null;
    } catch {
      return null;
    }
  });

  const [isLoading, setIsLoading] = useState(() => {
    try {
      return !localStorage.getItem(CACHE_KEY);
    } catch {
      return true;
    }
  });

  useEffect(() => {
    async function fetchTenantConfig() {
      try {
        const res = await axiosInstance.get('/api/tenants/config');
        if (res.data.tenant) {
          const nextTenant = normalizeTenant(res.data.tenant);
          setTenant(nextTenant);
          localStorage.setItem(CACHE_KEY, JSON.stringify(nextTenant));
        }
      } catch (error) {
        console.error('Failed to fetch tenant config', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchTenantConfig();
  }, []);

  useEffect(() => {
    applyThemeColors(tenant?.themeColors);
    notifyNativeSafeAreaTheme();
  }, [tenant?.themeColors]);

  return (
    <TenantContext.Provider
      value={{ tenant, isLoading, isWhitelabel: Boolean(tenant && tenant.slug !== 'milora') }}
    >
      {children}
    </TenantContext.Provider>
  );
}
